import type { Redirect } from 'next';
import type { SSRConfig } from 'next-i18next';
import type { DehydratedState } from '@tanstack/react-query';
import type { StatusCode } from '@kc-console/core';
import type { ResponseCloudProject, ResponseProvidedLanguage } from '../../api/iam/openapi';
import type {
  ENDPOINT_TYPE,
  ENV,
  GUARD_CHAIN_TYPE,
  MENU_SCOPE,
  PARTITION,
  REGION,
  REGION_SUPPORT,
  ROUTE_LAYOUT_TYPE,
  ROUTE_TYPE_TYPE,
  SKELETON_TYPE_TYPE,
  TOKEN_SCOPE,
} from '../common';

type ValueOf<T> = T[keyof T];

export type Region = ValueOf<typeof REGION>;
export type Partition = ValueOf<typeof PARTITION>;
export type RegionSupport = ValueOf<typeof REGION_SUPPORT>;
export type EndpointType = ValueOf<typeof ENDPOINT_TYPE>;
export type Env = ValueOf<typeof ENV>;
export type GuardChainType = ValueOf<typeof GUARD_CHAIN_TYPE>;
export type RouteTypeType = ValueOf<typeof ROUTE_TYPE_TYPE>;
export type SkeletonTypeType = ValueOf<typeof SKELETON_TYPE_TYPE>;
export type RouteLayoutType = ValueOf<typeof ROUTE_LAYOUT_TYPE>;
export type TokenScope = ValueOf<typeof TOKEN_SCOPE>;
export type MenuScope = ValueOf<typeof MENU_SCOPE>;
export type UserGuide = {
  [key: string]: string;
};
export type Language = ResponseProvidedLanguage;

export interface RequireParams {
  domainId: string;
  projectId?: string;
  region?: (typeof REGION)[keyof typeof REGION];
}

export interface BaseState {
  bffEndpoint: string;
  displayLanguage: string;
  env: Env;
  error: unknown;
  iamEndpoint: string;
  iamSSREndpoint: string;
  language: string;
  redirect: null | Redirect;
  serviceKeyword: string;
  ssrConfig: SSRConfig | null;
  status: StatusCode;
}

export interface State extends BaseState {
  domainId: string;
  domainName: string;
  isProjectForced: boolean;
  isRegionForced: boolean;
  projectId: string;
  projectName: string;
  scope: TokenScope;
  token: string;
  userId: string;
  userName: string;
  region?: Region;
}

export interface ServiceAppState extends State {
  accessibleProjects: ResponseCloudProject[];
  defaultProjectId: string;
}

/**
 * Guards 실행의 결과물 state 중 query로 올려주지 않거나 서버에서만 사용되고 버려지는 값.
 */
type ServerConsumedKeys =
  | 'error'
  | 'redirect'
  // gssp에서 props로 내려주므로 쿼리로 내려주지 않음.
  | 'ssrConfig'
  // client side에선 router.asPath로부터 serviceKeyword를 파싱하도록 한다.
  | 'serviceKeyword';

/**
 * Guards 실행의 결과물 state 중 initial/route 타입의 가드 모두 dehydrate하여 값을 올려주는 값.
 */
type ServiceAppRouteStateKeys = 'status' | 'language' | 'displayLanguage';

export type ServiceAppRouteState = Pick<ServiceAppState, ServiceAppRouteStateKeys>;

export type ServiceAppInitialState = Omit<
  ServiceAppState,
  ServiceAppRouteStateKeys | ServerConsumedKeys
>;

export interface PageComponentProps extends SSRConfig {
  dehydratedState?: DehydratedState;
}

export * from './api';
export * from './notification';
export * from './state';
export * from './stream';
export * from './task';
